import { collabStateManager } from '@databases/collab-state-manager';
import type { DatabaseAiFieldRunAwareness } from '@databases/collab-state-manager/y-database-telepointers';

import { hasOverlappingAiFieldRun } from './ai-field-run-awareness.ts';

export type ManualAiFieldRefreshBaseOptions = {
  abortSignal?: AbortSignal;
  contentId: string;
  fieldId: string;
  operation: string;
};

export type ManualAiFieldRefreshPreparation<TGenerationWriteContext, TRequestContext> = {
  eligibleEntryIds: readonly string[];
  generationWriteContext: TGenerationWriteContext;
  requestContext: TRequestContext;
};

export type ManualAiFieldRefreshResult =
  | { kind: 'ran' }
  | { kind: 'no-eligible-entries' }
  | { kind: 'overlapping-run' };

export type ManualAiFieldRefreshAdapter<
  TOptions extends ManualAiFieldRefreshBaseOptions,
  TGenerationWriteContext,
  TRequestContext
> = {
  aiFieldType: string;
  getAwarenessScope: (options: TOptions) => DatabaseAiFieldRunAwareness['scope'];
  prepare: (
    options: TOptions
  ) => Promise<ManualAiFieldRefreshPreparation<TGenerationWriteContext, TRequestContext>>;
  runEligibleEntries: (
    options: TOptions,
    eligibleEntryIds: readonly string[],
    generationWriteContext: TGenerationWriteContext,
    requestContext: TRequestContext
  ) => Promise<void>;
};

const minimumValidationFeedbackMs = 300;

const waitForValidationFeedback = async (validationStartedAt: number): Promise<void> => {
  const remainingMs = minimumValidationFeedbackMs - (Date.now() - validationStartedAt);
  if (remainingMs > 0) {
    await new Promise<void>((resolve) => {
      globalThis.setTimeout(resolve, remainingMs);
    });
  }
};

export async function runManualAiFieldRefresh<
  TOptions extends ManualAiFieldRefreshBaseOptions,
  TGenerationWriteContext,
  TRequestContext
>(
  options: TOptions,
  adapter: ManualAiFieldRefreshAdapter<TOptions, TGenerationWriteContext, TRequestContext>
): Promise<ManualAiFieldRefreshResult> {
  options.abortSignal?.throwIfAborted();

  if (adapter.aiFieldType.trim() === '') {
    throw new Error('AI field type must not be empty');
  }

  const manager = collabStateManager.getCollabManager(options.contentId);
  if (!manager) {
    throw new Error('Database not found');
  }
  const awarenessScope = adapter.getAwarenessScope(options);
  if (
    hasOverlappingAiFieldRun(manager.aiFieldRuns, {
      aiFieldType: adapter.aiFieldType,
      fieldId: options.fieldId,
      scope: awarenessScope
    }) ||
    (awarenessScope.kind === 'field' &&
      Object.values(manager.aiFieldCellRequests ?? {}).some(({ requests }) =>
        requests.some(
          (request) =>
            request.aiFieldType === adapter.aiFieldType &&
            request.fieldId === options.fieldId
        )
      ))
  ) {
    return { kind: 'overlapping-run' };
  }

  const runId = globalThis.crypto.randomUUID();
  const validationStartedAt = Date.now();
  manager.updateAiFieldRun({
    protocolVersion: 1,
    runId,
    aiFieldType: adapter.aiFieldType,
    fieldId: options.fieldId,
    phase: 'validating',
    scope: awarenessScope
  });

  let didFail = false;

  try {
    const preparation = await adapter.prepare(options);
    options.abortSignal?.throwIfAborted();
    if (preparation.eligibleEntryIds.length === 0) {
      return { kind: 'no-eligible-entries' };
    }

    manager.updateAiFieldRun({
      protocolVersion: 1,
      runId,
      aiFieldType: adapter.aiFieldType,
      fieldId: options.fieldId,
      phase: 'generating',
      scope: awarenessScope
    });

    await adapter.runEligibleEntries(
      options,
      preparation.eligibleEntryIds,
      preparation.generationWriteContext,
      preparation.requestContext
    );
    return { kind: 'ran' };
  } catch (error) {
    didFail = true;
    throw error;
  } finally {
    if (!didFail) {
      await waitForValidationFeedback(validationStartedAt);
    }
    const clientId = manager.clientId;
    if (clientId !== null && manager.aiFieldRuns[clientId]?.runId === runId) {
      manager.updateAiFieldRun(undefined);
    }
  }
}
